import React, { useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import AwsImage from '../../components/AwsImage';
import AgentFormPanel from './AgentFormPanel';
import ModalTestIA from './ModalTestIA';
import ModalZoneToggle from './ModalZoneToggle';
import {
    getAgentIntelligenceLevel,
    getIntelligenceLevelLabel,
    getTypeLabel
} from './agentOptions';
import {
    useCreateIAMutation,
    useDeleteIAMutation,
    useEditIAMutation,
    useFetchIAsAll
} from './Services/query';

const PROVIDER_COLORS = {
    OPENAI: '#10a37f',
    ANTHROPIC: '#c17b4e',
    AZURE: '#0078d4',
    GEMINI: '#4285f4',
    PERPLEXITY: '#6e56cf',
    CUSTOM: '#6b7280'
};

const IAsManagerScreen = () => {
    const { data: agents, isLoading, refetch } = useFetchIAsAll();
    const createMutation = useCreateIAMutation();
    const editMutation = useEditIAMutation();
    const deleteMutation = useDeleteIAMutation();
    const toast = useRef(null);
    const [view, setView] = useState('list');
    const [editingAgent, setEditingAgent] = useState(null);
    const [testAgent, setTestAgent] = useState(null);
    const [zoneAgent, setZoneAgent] = useState(null);
    const [search, setSearch] = useState('');

    const sortedAgents = useMemo(() => [...(agents || [])].sort((first, second) => {
        return new Date(second.createdAt || 0) - new Date(first.createdAt || 0) || second.id - first.id;
    }), [agents]);

    const filteredAgents = sortedAgents.filter((agent) => agent.name?.toLowerCase().includes(search.trim().toLowerCase()));
    const activeCount = sortedAgents.filter((agent) => agent.isActive).length;

    const openForm = (agent = null) => {
        setEditingAgent(agent);
        setTestAgent(agent);
        setView('form');
    };

    const closeForm = () => {
        setView('list');
        setEditingAgent(null);
        setTestAgent(null);
        refetch();
    };

    const confirmDelete = (agent) => confirmDialog({
        message: `Excluir o assistente "${agent.name}"? Esta ação não pode ser desfeita.`,
        header: 'Confirmar exclusão',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Excluir',
        rejectLabel: 'Cancelar',
        acceptClassName: 'p-button-danger',
        accept: async () => {
            try {
                await deleteMutation.mutateAsync(agent.id);
                toast.current?.show({ severity: 'success', summary: 'Assistente excluído', life: 2200 });
            } catch (error) {
                toast.current?.show({ severity: 'error', summary: 'Erro ao excluir', detail: error?.response?.data?.message || 'Não foi possível excluir o assistente.' });
            }
        }
    });

    if (view === 'form') {
        return (
            <>
                <Toast ref={toast} />
                <div className="flex surface-card" style={{ height: 'calc(100vh - 8rem)', minHeight: 620, overflow: 'hidden' }}>
                    <div className="flex-1 overflow-y-auto border-right-1 surface-border" style={{ minWidth: 0 }}>
                        <AgentFormPanel
                            agent={editingAgent}
                            onBack={closeForm}
                            toast={toast}
                            createAgent={(body) => createMutation.mutateAsync(body)}
                            editAgent={(body) => editMutation.mutateAsync(body)}
                            onSaved={(saved) => {
                                setEditingAgent(saved);
                                setTestAgent(saved);
                                refetch();
                            }}
                        />
                    </div>
                    <div className="hidden lg:flex flex-column" style={{ width: 460, flexShrink: 0 }}>
                        <ModalTestIA agent={testAgent} />
                    </div>
                </div>
            </>
        );
    }

    return (
        <div className="p-4" style={{ maxWidth: 920, margin: '0 auto' }}>
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="flex align-items-start justify-content-between gap-3 mb-4">
                <div>
                    <h2 className="m-0 text-2xl font-semibold">Assistentes de IA</h2>
                    <div className="text-color-secondary text-sm mt-2">
                        {sortedAgents.length} configurado{sortedAgents.length !== 1 ? 's' : ''} · {activeCount} ativo{activeCount !== 1 ? 's' : ''} · {sortedAgents.length - activeCount} inativo{sortedAgents.length - activeCount !== 1 ? 's' : ''}
                    </div>
                </div>
                <Button label="Novo Assistente" icon="pi pi-plus" onClick={() => openForm()} />
            </div>

            <span className="p-input-icon-left w-full mb-4">
                <i className="pi pi-search" />
                <InputText value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar assistente..." className="w-full" />
            </span>

            {isLoading ? (
                <div className="flex justify-content-center p-6"><i className="pi pi-spin pi-spinner text-primary" style={{ fontSize: '2rem' }} /></div>
            ) : filteredAgents.length === 0 ? (
                <div className="surface-card border-2 border-dashed surface-border border-round-xl text-center p-6 text-color-secondary">
                    <i className="pi pi-android block mb-3" style={{ fontSize: '2.75rem', opacity: 0.25 }} />
                    <div className="font-medium text-color">{search ? 'Nenhum assistente encontrado' : 'Nenhum assistente criado ainda'}</div>
                    {!search && <Button label="Criar assistente" icon="pi pi-plus" outlined className="mt-4" onClick={() => openForm()} />}
                </div>
            ) : (
                <div className="flex flex-column gap-2">
                    {filteredAgents.map((agent) => {
                        const providerColor = PROVIDER_COLORS[agent.provider] || '#6b7280';
                        return (
                            <div key={agent.id} className="surface-card border-1 surface-border border-round-xl p-3 flex align-items-center gap-3 shadow-1">
                                <div className="border-round-lg overflow-hidden flex align-items-center justify-content-center" style={{ width: 44, height: 44, flexShrink: 0, background: `${providerColor}18` }}>
                                    {agent.aws_fk || agent.aws?.id || agent.aws?.url ? (
                                        <AwsImage
                                            awsId={agent.aws_fk ?? agent.aws?.id}
                                            aws={agent.aws}
                                            usage="avatar"
                                            width={44}
                                            height={44}
                                            alt={agent.name}
                                            fallback={<i className="pi pi-android" style={{ color: providerColor, fontSize: '1.25rem' }} />}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                    ) : <i className="pi pi-android" style={{ color: providerColor, fontSize: '1.25rem' }} />}
                                </div>

                                <div className="flex-1" style={{ minWidth: 0 }}>
                                    <div className="flex align-items-center gap-2 flex-wrap">
                                        <span className="font-semibold white-space-nowrap overflow-hidden text-overflow-ellipsis">{agent.name}</span>
                                        <span className="text-xs font-semibold border-round-xl px-2 py-1" style={{ background: `${providerColor}18`, color: providerColor }}>{agent.provider}</span>
                                        <span className="text-xs font-semibold border-round-xl px-2 py-1" style={{ background: agent.isActive ? '#10b98118' : '#f59e0b18', color: agent.isActive ? '#059669' : '#d97706' }}>{agent.isActive ? 'Ativo' : 'Inativo'}</span>
                                    </div>
                                    <div className="flex gap-3 flex-wrap text-sm text-color-secondary mt-2">
                                        <span>{getIntelligenceLevelLabel(getAgentIntelligenceLevel(agent))}</span>
                                        <span>{getTypeLabel(agent.type)}</span>
                                        <span>Temp: {agent.temperature}</span>
                                        <span>ID: {agent.id}</span>
                                    </div>
                                </div>

                                <div className="flex gap-1">
                                    <Button icon="pi pi-play" rounded text severity="info" tooltip="Testar assistente" onClick={() => openForm(agent)} />
                                    <Button icon="pi pi-sitemap" rounded text severity="secondary" tooltip="Vincular a zonas" onClick={() => setZoneAgent(agent)} />
                                    <Button icon="pi pi-pencil" rounded text severity="secondary" tooltip="Editar" onClick={() => openForm(agent)} />
                                    <Button icon="pi pi-trash" rounded text severity="danger" tooltip="Excluir" onClick={() => confirmDelete(agent)} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <ModalZoneToggle agent={zoneAgent} onHide={() => setZoneAgent(null)} toast={toast} />
        </div>
    );
};

export default IAsManagerScreen;
